import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Check, Newspaper, Calendar, CheckCheck } from 'lucide-react';
import * as Lucide from 'lucide-react';
import { User } from '../types';

interface NewsItem {
    id: number;
    title: string;
    content: string;
    icon: string;
    category: string;
    image_url?: string;
    created_at?: string;
}

// Dynamic Icon Renderer helper
const renderNewsIcon = (iconStr: string, className = "w-8 h-8") => {
    if (!iconStr) return <Newspaper className={className} />;
    
    let cleanName = iconStr.trim();
    if (iconStr.includes('className')) {
        const match = iconStr.match(/<([A-Za-z0-9]+)/);
        if (match) cleanName = match[1];
    }
    
    const IconComponent = (Lucide as any)[cleanName];
    if (IconComponent) {
        return <IconComponent className={className} />;
    }
    
    // Emojis or raw string fallback
    return <span className="text-3xl inline-block">{iconStr}</span>;
};

interface NewsOverlayProps {
    user: User | null;
}

const NewsOverlay: React.FC<NewsOverlayProps> = ({ user }) => {
    const [newsItems, setNewsItems] = useState<NewsItem[]>([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [tempClosed, setTempClosed] = useState(false);
    const [loading, setLoading] = useState(true);
    const [isConfirming, setIsConfirming] = useState(false);

    const fetchNews = useCallback(async () => {
        try {
            const token = localStorage.getItem('xc_auth_token');
            const headers: HeadersInit = {};
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const res = await fetch('/api/news', { headers });
            if (res.ok) {
                const data: NewsItem[] = await res.json();
                
                // Filter out news that have already been dismissed in localStorage
                const dismissedIdsStr = localStorage.getItem('xc_dismissed_news_ids');
                const dismissedIds: number[] = dismissedIdsStr ? JSON.parse(dismissedIdsStr) : [];
                
                const activeUndismissed = data.filter((item: NewsItem) => !dismissedIds.includes(item.id));
                setNewsItems(activeUndismissed);
                setCurrentIndex(0);
            }
        } catch (e) {
            console.error("Failed to load news for overlay:", e);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchNews();

        const handleReload = () => {
            fetchNews();
        };

        window.addEventListener('news-created-reload', handleReload);
        return () => {
            window.removeEventListener('news-created-reload', handleReload);
        };
    }, [user, fetchNews]);

    if (loading || tempClosed || newsItems.length === 0) {
        return null;
    }

    const currentItem = newsItems[currentIndex] || newsItems[0];
    if (!currentItem) {
        return null;
    }

    const handleConfirmRead = async () => {
        if (isConfirming) return;
        setIsConfirming(true);

        const targetId = currentItem.id;

        try {
            // 1. Add current item ID to dismissed list in localStorage
            const dismissedIdsStr = localStorage.getItem('xc_dismissed_news_ids');
            const dismissedIds: number[] = dismissedIdsStr ? JSON.parse(dismissedIdsStr) : [];
            if (!dismissedIds.includes(targetId)) {
                dismissedIds.push(targetId);
                localStorage.setItem('xc_dismissed_news_ids', JSON.stringify(dismissedIds));
            }

            // 2. If user is logged in, sync with database
            if (user) {
                const token = localStorage.getItem('xc_auth_token');
                if (token) {
                    await fetch('/api/news/read', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify({ newsId: targetId })
                    }).catch(e => console.error("Failed to mark news as read in DB:", e));
                }
            }

            // 3. Remove this news item from active list immediately
            const remainingItems = newsItems.filter(item => item.id !== targetId);
            setNewsItems(remainingItems);

            if (remainingItems.length === 0) {
                // All news acknowledged
                setTempClosed(true);
            } else {
                // Adjust index if we were at the end of the list
                if (currentIndex >= remainingItems.length) {
                    setCurrentIndex(remainingItems.length - 1);
                }
            }
        } finally {
            setIsConfirming(false);
        }
    };

    const handleNext = () => {
        if (currentIndex < newsItems.length - 1) {
            setCurrentIndex(prev => prev + 1);
        }
    };

    const handlePrev = () => {
        if (currentIndex > 0) {
            setCurrentIndex(prev => prev - 1);
        }
    };

    return (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md animate-in fade-in duration-300">
            {/* Main Modal Card */}
            <div className="bg-slate-900/95 border border-emerald-500/30 shadow-[0_20px_50px_rgba(16,185,129,0.2)] rounded-3xl p-6 max-w-lg w-full relative overflow-hidden animate-in zoom-in-95 duration-300 flex flex-col max-h-[85vh]">
                
                {/* Glow Background effect */}
                <div className="absolute -top-24 -left-24 w-48 h-48 bg-emerald-500/10 rounded-full blur-[60px] pointer-events-none" />
                <div className="absolute -bottom-24 -right-24 w-48 h-48 bg-sky-500/10 rounded-full blur-[60px] pointer-events-none" />

                {/* News Header Section */}
                <div className="flex items-center justify-between gap-4 mb-6 shrink-0 relative">
                    <div className="flex items-center gap-4">
                        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500/20 to-emerald-600/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400 shrink-0 shadow-inner">
                            {renderNewsIcon(currentItem.icon, "w-7 h-7")}
                        </div>
                        <div>
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="px-2.5 py-0.5 rounded bg-emerald-500/10 border border-emerald-500/20 text-[10px] font-black uppercase tracking-wider text-emerald-400">
                                    {currentItem.category || 'News'}
                                </span>
                                {newsItems.length > 1 && (
                                    <span className="px-2 py-0.5 rounded bg-amber-500/10 border border-amber-500/20 text-[10px] font-bold text-amber-400">
                                        {newsItems.length} ungelesen
                                    </span>
                                )}
                            </div>
                            {currentItem.created_at && (
                                <div className="text-[11px] text-slate-400 font-semibold mt-1 uppercase tracking-wide inline-flex items-center gap-1">
                                    <Calendar className="w-3 h-3 text-slate-500" /> {new Date(currentItem.created_at).toLocaleDateString('de-DE')}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Scrollable Content */}
                <div className="flex-1 overflow-y-auto custom-scrollbar mb-6 pr-1 relative">
                    <h3 className="text-2xl font-black text-white leading-tight mb-4 tracking-tight uppercase">
                        {currentItem.title}
                    </h3>
                    {currentItem.image_url && (
                        <div className="mb-4 rounded-2xl overflow-hidden border border-white/10 bg-slate-950/40">
                            <img 
                                src={currentItem.image_url} 
                                alt={currentItem.title} 
                                className="w-full max-h-64 object-cover"
                            />
                        </div>
                    )}
                    <p className="text-slate-300 text-[15px] leading-relaxed whitespace-pre-wrap font-medium">
                        {currentItem.content}
                    </p>
                </div>

                {/* Footer section with Pagination and Actions */}
                <div className="flex items-center justify-between pt-4 border-t border-white/5 shrink-0 gap-3">
                    {/* Pagination Indicator / Arrows */}
                    <div className="flex items-center gap-2">
                        {newsItems.length > 1 ? (
                            <>
                                <button
                                    onClick={handlePrev}
                                    disabled={currentIndex === 0 || isConfirming}
                                    title="Vorherige News ansehen"
                                    className="w-8 h-8 rounded-lg bg-slate-800 hover:bg-slate-700 disabled:opacity-30 disabled:hover:bg-slate-800 text-slate-300 flex items-center justify-center border border-white/5 transition-all"
                                >
                                    <ChevronLeft className="w-4 h-4" />
                                </button>
                                <span className="text-xs text-slate-400 font-bold uppercase tracking-wider px-1 select-none">
                                    {currentIndex + 1} / {newsItems.length}
                                </span>
                                <button
                                    onClick={handleNext}
                                    disabled={currentIndex === newsItems.length - 1 || isConfirming}
                                    title="Nächste News ansehen"
                                    className="w-8 h-8 rounded-lg bg-slate-800 hover:bg-slate-700 disabled:opacity-30 disabled:hover:bg-slate-800 text-slate-300 flex items-center justify-center border border-white/5 transition-all"
                                >
                                    <ChevronRight className="w-4 h-4" />
                                </button>
                            </>
                        ) : (
                            <span className="text-xs text-emerald-400/80 font-bold uppercase tracking-wider flex items-center gap-1.5">
                                <CheckCheck className="w-4 h-4 text-emerald-400" /> Letzte Meldung
                            </span>
                        )}
                    </div>

                    {/* Primary Confirmation Button */}
                    <button
                        onClick={handleConfirmRead}
                        disabled={isConfirming}
                        className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-500 hover:to-emerald-400 disabled:opacity-50 text-white rounded-xl font-black text-xs uppercase tracking-wider transition-all shadow-[0_10px_20px_rgba(16,185,129,0.2)] hover:shadow-[0_10px_25px_rgba(16,185,129,0.3)] hover:-translate-y-0.5 active:translate-y-0"
                    >
                        <Check className="w-4 h-4 stroke-[3]" />
                        {newsItems.length > 1 
                            ? 'Als gelesen bestätigen' 
                            : 'Als gelesen bestätigen & Schließen'
                        }
                    </button>
                </div>
            </div>
        </div>
    );
};

export default NewsOverlay;
